import { c as _c } from "react/compiler-runtime";
import type { NotificationWithReadState } from '@/data/notifications/types';
import { useCallback, useEffect, useRef } from 'react';
type AggregationMap = Map<number, {
  parentId: string;
  childIds: Set<string>;
  lastUpdated: Date;
}>;

/**
 * Extracts a PR number from a notification using various methods
 */
export function extractPrNumber(notification: NotificationWithReadState): number | null {
  const eventData = notification.eventData as any;

  // Direct PR number in event data (standard format)
  if (eventData && typeof eventData.number === 'number') {
    return eventData.number;
  }

  // Check title for PR number pattern (#123)
  const titlePrMatch = notification.title.match(/#(\d+)/);
  if (titlePrMatch && titlePrMatch[1]) {
    return Number.parseInt(titlePrMatch[1], 10);
  }

  // Check message for PR number pattern
  const messagePrMatch = notification.message.match(/#(\d+)/);
  if (messagePrMatch && messagePrMatch[1]) {
    return Number.parseInt(messagePrMatch[1], 10);
  }

  // Check URL for PR number pattern
  if (notification.actionUrl) {
    const urlPrMatch = notification.actionUrl.match(/pull\/(\d+)/);
    if (urlPrMatch && urlPrMatch[1]) {
      return Number.parseInt(urlPrMatch[1], 10);
    }
  }
  return null;
}

/**
 * Hook to manage notification aggregation state
 * Maintains a persistent map of PR numbers to their aggregated notifications
 */
export function useNotificationAggregation(allNotifications, onPreUpdate, onPostUpdate) {
  const $ = _c(10);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = new Map();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const aggregationMapRef = useRef(t0);
  let t1;
  let t2;
  if ($[1] !== allNotifications || $[2] !== onPostUpdate || $[3] !== onPreUpdate) {
    t1 = () => {
      if (!allNotifications.length) {
        return;
      }
      if (onPreUpdate) {
        onPreUpdate();
      }
      const aggregationMap = aggregationMapRef.current;
      const notificationById = new Map();
      for (const notification of allNotifications) {
        if (!notificationById.has(notification.id)) {
          notificationById.set(notification.id, notification);
        }
      }
      allNotifications.forEach(notification_0 => {
        if (notification_0.isRead) {
          return;
        }
        const prNumber = extractPrNumber(notification_0);
        if (!prNumber) {
          return;
        }
        if (aggregationMap.has(prNumber)) {
          const aggregation = aggregationMap.get(prNumber);
          if (notification_0.id === aggregation.parentId || aggregation.childIds.has(notification_0.id)) {
            return;
          }
          const existingParent = notificationById.get(aggregation.parentId);
          const notificationDate = new Date(notification_0.createdAt);
          if (existingParent && notificationDate > new Date(existingParent.createdAt)) {
            aggregation.childIds.add(aggregation.parentId);
            aggregation.parentId = notification_0.id;
          } else {
            aggregation.childIds.add(notification_0.id);
          }
          aggregation.lastUpdated = new Date();
        } else {
          aggregationMap.set(prNumber, {
            parentId: notification_0.id,
            childIds: new Set(),
            lastUpdated: new Date()
          });
        }
      });
      for (const [prNumber_0, aggregation_0] of Array.from(aggregationMap.entries())) {
        const parentNotif = notificationById.get(aggregation_0.parentId);
        if (!parentNotif || parentNotif.isRead) {
          const unreadChildren = [];
          for (const id of aggregation_0.childIds) {
            const child = notificationById.get(id);
            if (child && !child.isRead) {
              unreadChildren.push(child);
            }
          }
          if (unreadChildren.length > 0) {
            unreadChildren.sort(_temp);
            const newParent = unreadChildren[0];
            aggregation_0.parentId = newParent.id;
            aggregation_0.childIds.delete(newParent.id);
            if (parentNotif) {
              aggregation_0.childIds.add(parentNotif.id);
            }
          } else {
            aggregationMap.delete(prNumber_0);
          }
        }
      }
      if (onPostUpdate) {
        onPostUpdate();
      }
    };
    t2 = [allNotifications, onPreUpdate, onPostUpdate];
    $[1] = allNotifications;
    $[2] = onPostUpdate;
    $[3] = onPreUpdate;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = (notifications, t4) => {
      const filter = t4 === undefined ? "all" : t4;
      if (!notifications.length) {
        return [];
      }
      const aggregationMap_0 = aggregationMapRef.current;
      const result = [];
      const processedIds = new Set();
      const notificationById_0 = new Map();
      for (const notification_1 of notifications) {
        if (!notificationById_0.has(notification_1.id)) {
          notificationById_0.set(notification_1.id, notification_1);
        }
      }
      for (const [_, aggregation_1] of aggregationMap_0.entries()) {
        const parentNotification = notificationById_0.get(aggregation_1.parentId);
        if (!parentNotification) {
          continue;
        }
        processedIds.add(parentNotification.id);
        const childNotifications = [];
        for (const id_0 of aggregation_1.childIds) {
          const child_0 = notificationById_0.get(id_0);
          if (child_0) {
            childNotifications.push(child_0);
          }
        }
        childNotifications.forEach(child_1 => processedIds.add(child_1.id));
        const enhancedNotification = {
          ...parentNotification,
          isAggregated: true,
          childNotifications: childNotifications.sort(_temp2)
        };
        result.push(enhancedNotification);
      }
      notifications.forEach(notification_2 => {
        if (!processedIds.has(notification_2.id)) {
          result.push(notification_2);
        }
      });
      const sortedResult = result.sort(_temp3);
      if (filter === "all") {
        return sortedResult;
      }
      if (filter === "unread") {
        return sortedResult.filter(_temp4);
      }
      if (filter === "read") {
        return sortedResult.filter(_temp5);
      }
      return sortedResult;
    };
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const getAggregatedNotifications = t3;
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = prNumber_1 => {
      if (aggregationMapRef.current.has(prNumber_1)) {
        aggregationMapRef.current.delete(prNumber_1);
        return true;
      }
      return false;
    };
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const removeAggregation = t4;
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      aggregationMapRef.current.clear();
    };
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const clearAggregations = t5;
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      getAggregatedNotifications,
      removeAggregation,
      clearAggregations,
      aggregationMapRef,
      extractPrNumber
    };
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
}
function _temp5(notification_4) {
  return notification_4.isRead;
}
function _temp4(notification_3) {
  return !notification_3.isRead;
}
function _temp3(a_1, b_1) {
  if (a_1.isRead !== b_1.isRead) {
    return a_1.isRead ? 1 : -1;
  }
  return new Date(b_1.createdAt).getTime() - new Date(a_1.createdAt).getTime();
}
function _temp2(a_0, b_0) {
  return new Date(b_0.createdAt).getTime() - new Date(a_0.createdAt).getTime();
}
function _temp(a, b) {
  return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
}